import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ReactComponent as InfoIcon } from "../../assets/svg/icons/info.svg";
import { ButtonOnlyTitle } from "../../components/Buttons/ButtonOnlyTitle/Index";
import { RoundedCloseButton } from "../../components/Buttons/RoundedCloseButton/Index";
import ChipPressableComponent from "../../components/Chips/ComponentChipPressable/Index";
import { StaticMapRender } from "../../components/EditOnboarding/Components/StaticMapRender/Index";
import { DropdownV2, InputV2, OptionV2 } from "../../components/FormControlsV2";
import { Loading } from "../../components/Loading/Index";
import { Modal } from "../../components/Modal/Default/Index";
import { useTranslation } from "react-i18next";
import Pagination from "../../components/Pagination";
import ViewTasks from "../../components/Tasks/ViewTasks";
import { TASKLIST } from "../../components/Tasks/TasksModals/Index";
import { useTaskCreation } from "../../hooks/useTaskCreation";
import {
  TASK_REQUEST_REOPEN_EVENT,
  TASK_REQUEST_UPDATED_EVENT,
  TaskRequest,
  TaskRequestStatus,
  taskRequestService,
} from "../../services/taskRequestService";
import {
  formatDateToCustomFormat,
  formatDateWithoutHour,
  formatHour,
  parseUtcDate,
} from "../../utils/date/formatedDate";
import "./styles.css";

const PAGE_SIZE = 10;

const STATUS_KEYS: Record<TaskRequestStatus, string> = {
  PENDING: "pending",
  ACCEPTED: "accepted",
  COMPLETED: "completed",
  REJECTED: "rejected",
  ARCHIVE: "archived",
};

function formatCreatedAt(value: string) {
  const date = parseUtcDate(value);
  return date ? formatDateToCustomFormat(date) : "-";
}

function renderRequestDate(value: string) {
  const date = parseUtcDate(value);
  if (!date || Number.isNaN(date.getTime())) return "";

  return (
    <>
      {formatDateWithoutHour(date)}
      <br />
      {formatHour(date)}
    </>
  );
}

export function TaskRequestsPage() {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<TaskRequest[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [statusFilter, setStatusFilter] = useState<TaskRequestStatus>();
  const [taskTypeFilter, setTaskTypeFilter] =
    useState<OptionV2<TaskType> | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<TaskRequest>();
  const [taskId, setTaskId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const {
    setPresetSelection,
    setSelectedTask,
    setShowCreateTaskModal,
    setSourceRequestId,
  } = useTaskCreation();
  const statusLabel = (status: TaskRequestStatus) =>
    t(`taskRequests.statuses.${STATUS_KEYS[status]}`);
  const taskTypeLabel = (type: TaskType) =>
    TASKLIST.find((task) => task.type === type)?.title || type;
  const taskTypeOptions: OptionV2<TaskType>[] = TASKLIST.map((task) => ({
    label: taskTypeLabel(task.type),
    value: task.type,
  }));

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await taskRequestService.list({
        page: currentPage - 1,
        size: PAGE_SIZE,
        status: statusFilter,
        taskType: taskTypeFilter?.value,
      });
      setRequests(response.content || []);
      setTotalPages(response.totalPages || 0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, taskTypeFilter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    window.addEventListener(TASK_REQUEST_UPDATED_EVENT, loadRequests);
    return () =>
      window.removeEventListener(TASK_REQUEST_UPDATED_EVENT, loadRequests);
  }, [loadRequests]);

  useEffect(() => {
    const reopenRequest = (event: Event) => {
      const requestId = (event as CustomEvent<number>).detail;
      if (!requestId) return;

      setShowRejectForm(false);
      setRejectionReason("");
      openRequest(requestId);
    };

    window.addEventListener(TASK_REQUEST_REOPEN_EVENT, reopenRequest);
    return () =>
      window.removeEventListener(TASK_REQUEST_REOPEN_EVENT, reopenRequest);
  }, []);

  const mapCoords = useMemo(
    () =>
      selectedRequest?.micro_area.coords.map((coord) => ({
        lat: Number(coord.latitude),
        lng: Number(coord.longitude),
      })) || [],
    [selectedRequest],
  );

  async function openRequest(id: number) {
    const request = await taskRequestService.getById(id);
    setSelectedRequest(request);
  }

  function handleStatusChange(status?: TaskRequestStatus) {
    setStatusFilter(status);
    setCurrentPage(1);
  }

  function handleTaskTypeChange(option: OptionV2<TaskType> | null) {
    setTaskTypeFilter(option);
    setCurrentPage(1);
  }

  async function rejectRequest() {
    if (!selectedRequest) return;

    setRejecting(true);
    try {
      await taskRequestService.reject(
        selectedRequest.id,
        rejectionReason.trim(),
      );
      setShowRejectForm(false);
      setRejectionReason("");
      setSelectedRequest(undefined);
      taskRequestService.notifyUpdated();
    } finally {
      setRejecting(false);
    }
  }

  function convertRequestToTask() {
    if (!selectedRequest) return;
    const task = TASKLIST.find(
      (item) => item.type === selectedRequest.task_type,
    );
    if (!task) return;

    setSourceRequestId(selectedRequest.id);
    setPresetSelection({
      farmId: selectedRequest.micro_area.farm_id,
      areaId: selectedRequest.micro_area.area_id,
      microAreaId: selectedRequest.micro_area.id,
      microAreaFullInitials: selectedRequest.micro_area.full_initials,
    });
    setSelectedTask(task);
    setSelectedRequest(undefined);
    setShowCreateTaskModal(true);
  }

  return (
    <>
      <div className="filter__content taskRequests__filters">
        <ChipPressableComponent
          theme={statusFilter === undefined ? "menu-dark" : "menu-light"}
          label={t("taskRequests.all")}
          onClick={() => handleStatusChange()}
        />
        {Object.keys(STATUS_KEYS).filter(status => status !== "COMPLETED").map((status) => (
          <ChipPressableComponent
            key={status}
            theme={statusFilter === status ? "menu-dark" : "menu-light"}
            label={statusLabel(status as TaskRequestStatus)}
            onClick={() => handleStatusChange(status as TaskRequestStatus)}
          />
        ))}
        <DropdownV2
          name="task-request-type"
          value={taskTypeFilter}
          options={taskTypeOptions}
          placeholder={t("taskRequests.allTypes")}
          clearOptionLabel={t("taskRequests.allTypes")}
          searchable
          menuMount="overlay"
          onChange={handleTaskTypeChange}
        />
      </div>

      <div className="taskRequests__tableWrapper">
        {requests.length > 0 ? (
          <>
            <table className="tableTaskEvaluation taskRequests__table">
              <thead>
                <tr>
                  <th>
                    <div>
                      {t("taskRequests.requestDateLine1")}
                      <br />
                      {t("taskRequests.requestDateLine2")}
                    </div>
                  </th>
                  <th>{t("taskRequests.user")}</th>
                  <th>{t("taskRequests.type")}</th>
                  <th>{t("taskRequests.paddock")}</th>
                  <th>{t("taskRequests.description")}</th>
                  <th>{t("taskRequests.status")}</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request.id} onClick={() => openRequest(request.id)}>
                    <td>{renderRequestDate(request.created_at)}</td>
                    <td>{request.created_by.name}</td>
                    <td>
                      {taskTypeLabel(request.task_type)}
                    </td>
                    <td>{request.micro_area.full_initials}</td>
                    <td
                      className="taskRequests__description"
                      title={request.description}
                    >
                      {request.description}
                    </td>
                    <td>
                      <span
                        className={`taskRequests__status taskRequests__status--${request.status.toLowerCase()}`}
                      >
                        {statusLabel(request.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {loading && (
              <div className="taskRequests__loadingOverlay">
                <Loading visible />
              </div>
            )}
          </>
        ) : (
          <div className="emptyTable_container">
            {loading ? (
              <Loading visible />
            ) : (
              <span>{t("taskRequests.noResults")}</span>
            )}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onClick={setCurrentPage}
        />
      )}

      <Modal
        visible={!!selectedRequest}
        contentStyle={
          {
            position: "relative",
            top: "auto",
            right: "auto",
            bottom: "auto",
            left: "auto",
            width: 760,
            maxWidth: "calc(100vw - 30px)",
            height: "fit-content",
            maxHeight: "calc(100vh - 30px)",
            margin: 0,
            padding: 40,
            borderRadius: 20,
            overflow: "auto",
          } as CSSProperties
        }
      >
        {selectedRequest && (
          <section className="taskRequests__modal">
            <header>
              <div>
                <h2>{t("taskRequests.request")} {statusLabel(selectedRequest.status)}</h2>
                <span>{selectedRequest.micro_area.full_initials}</span>
              </div>
              <RoundedCloseButton
                onClick={() => {
                  setShowRejectForm(false);
                  setRejectionReason("");
                  setSelectedRequest(undefined);
                }}
              />
            </header>

            <div className="delimiter" />
            <dl>
              <div>
                <dt>{t("taskRequests.type")}</dt>
                <dd>{taskTypeLabel(selectedRequest.task_type)}</dd>
              </div>
              <div>
                <dt>{t("taskRequests.requestDate")}</dt>
                <dd>{formatCreatedAt(selectedRequest.created_at)}</dd>
              </div>
              <div>
                <dt>{t("taskRequests.createdBy")}</dt>
                <dd>{selectedRequest.created_by.name}</dd>
              </div>

              {selectedRequest.converted_at && (
                <div>
                  <dt>{t("taskRequests.conversionDate")}</dt>
                  <dd>{formatCreatedAt(selectedRequest.converted_at)}</dd>
                </div>
              )}

              <div
                className={
                  selectedRequest.status === "COMPLETED" &&
                  selectedRequest.converted_task_id
                    ? undefined
                    : "taskRequests__details--full"
                }
              >
                <dt>{t("taskRequests.description")}</dt>
                <dd>{selectedRequest.description}</dd>
              </div>

              {selectedRequest.status === "COMPLETED" &&
                selectedRequest.converted_task_id && (
                  <div className="taskRequests__viewTaskButton">
                    <ButtonOnlyTitle
                      title={t("taskRequests.viewTask")}
                      theme="dark"
                      onClick={() => {
                        setTaskId(selectedRequest.converted_task_id!);
                        setSelectedRequest(undefined);
                      }}
                    />
                  </div>
                )}

              {selectedRequest.rejection_reason && (
                <div className="taskRequests__details--full">
                  <dt>{t("taskRequests.rejectionReason")}</dt>
                  <dd>{selectedRequest.rejection_reason}</dd>
                </div>
              )}
            </dl>

            {selectedRequest.status === "ARCHIVE" &&
              <>
                <div className="taskRequests__details--full">
                  <dd>{t("taskRequests.archivedAutomatically")}</dd>
                </div>
                <br/>
              </>
            }

            {mapCoords.length > 0 && (
              <div className="taskRequests__map">
                <StaticMapRender mainPolygon={mapCoords} microAreas={[]} />
              </div>
            )}

            {selectedRequest.status === "PENDING" && (
              <>
                {!showRejectForm ? (
                  <footer>
                    <ButtonOnlyTitle
                      title={t("taskRequests.reject")}
                      theme="light"
                      onClick={() => {
                        setRejectionReason("");
                        setShowRejectForm(true);
                      }}
                    />
                    <ButtonOnlyTitle
                      title={t("taskRequests.createTask")}
                      theme="dark"
                      onClick={convertRequestToTask}
                    />
                  </footer>
                ) : (
                  <div className="taskRequests__rejectForm">
                    <InputV2
                      name="task-request-rejection-reason"
                      label={t("taskRequests.rejectionReason")}
                      type="textarea"
                      value={rejectionReason}
                      maxLength={500}
                      disabled={rejecting}
                      onChange={setRejectionReason}
                    />
                    <div className="taskRequests__rejectActions">
                      <ButtonOnlyTitle
                        title={t("taskRequests.cancel")}
                        theme="light"
                        disabled={rejecting}
                        onClick={() => {
                          setRejectionReason("");
                          setShowRejectForm(false);
                        }}
                      />
                      <ButtonOnlyTitle
                        title={t("taskRequests.confirm")}
                        theme="urgent"
                        disabled={rejecting}
                        onClick={rejectRequest}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        )}
      </Modal>

      <ViewTasks selected={taskId} setSelectedId={setTaskId} />
    </>
  );
}
